import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePersistentCart } from '../context/PersistentCartContext';
import rateLimiter from '../utils/rateLimiter';

/**
 * Hook que sincroniza el carrito con el estado de autenticación
 */
export const useCartAuth = () => {
  const { isAuthenticated, user } = useAuth();
  const { syncCart, clearCart, state: cartState } = usePersistentCart();
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasInitializedRef = useRef(false);
  const lastUserIdRef = useRef<number | null>(null);

  // Sincronizar carrito al hacer login (con rate limiting global)
  useEffect(() => {
    // Evitar sync en el primer render
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      lastUserIdRef.current = user?.id || null;
      console.log('🔐 useCartAuth: Primera inicialización, saltando sync automático');
      return;
    }

    // Solo sincronizar si el usuario cambió realmente
    const currentUserId = user?.id || null;
    const userChanged = currentUserId !== lastUserIdRef.current;
    
    if (!userChanged) {
      console.log('🔐 useCartAuth: Usuario no cambió, saltando sync');
      return;
    }

    console.log(`🔐 useCartAuth: Usuario cambió de ${lastUserIdRef.current} a ${currentUserId}`);
    lastUserIdRef.current = currentUserId;

    if (isAuthenticated && user) {
      console.log('🔐 useCartAuth: Usuario autenticado detectado, verificando rate limiting...');
      
      // Limpiar timeout anterior si existe
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
      
      // Usar el rate limiter global con timeout más largo
      syncTimeoutRef.current = setTimeout(async () => {
        try {
          console.log('🔐 useCartAuth: Solicitando sincronización vía rateLimiter...');
          
          const result = await rateLimiter.executeSync(async () => {
            console.log('🔐 useCartAuth: Ejecutando syncCart dentro de rateLimiter...');
            return await syncCart();
          });
          
          if (result === null) {
            console.log('🔐 useCartAuth: Sincronización BLOQUEADA por rate limiting');
            const status = rateLimiter.getStatus();
            console.log('🔐 useCartAuth: Estado del rateLimiter:', status);
          } else {
            console.log('🔐 useCartAuth: Sincronización COMPLETADA exitosamente');
          }
        } catch (error) {
          console.error('❌ useCartAuth: Error sincronizando carrito:', error);
        }
      }, 3000); // Aumentado a 3 segundos para dar más tiempo
    }

    // Cleanup function
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = null;
      }
    };
  }, [isAuthenticated, user?.id, syncCart]);

  // Opcional: Limpiar carrito al hacer logout (comentado para mantener persistencia)
  useEffect(() => {
    if (!isAuthenticated && user === null && lastUserIdRef.current !== null) {
      console.log('🔐 useCartAuth: Usuario desautenticado, manteniendo carrito local');
      lastUserIdRef.current = null;
      
      // OPCIÓN 1: Mantener carrito (recomendado para mejor UX)
      // No hacer nada - el carrito se mantiene para cuando el usuario regrese
      
      // OPCIÓN 2: Limpiar carrito (descomentar si se desea este comportamiento)
      // clearCart().catch((error: any) => {
      //   console.error('❌ Error limpiando carrito después del logout:', error);
      // });
    }
  }, [isAuthenticated, user, clearCart]);

  return {
    isCartSynced: cartState.isOnline,
    cartItemsCount: cartState.itemsCount,
    cartSubtotal: cartState.subtotal,
    syncCart,
    clearCart
  };
};
